// Bridges the QA reporter portals past every MUI focus trap in the app.
//
// The QA dialog/annotator render via createPortal to document.body, OUTSIDE any MUI
// <Modal>/<Menu>/<Popover> subtree. MUI's focus traps (enforceFocus) listen for
// `focusin` on `document` (bubble phase) and, when focus lands outside their own
// root, yank it back. So when a QA portal opens on top of an app modal (e.g. the
// Create Campaign modal, or one of its Menu/Autocomplete dropdowns) the QA inputs
// can't hold focus and typing is lost.
//
// Rather than patch disableEnforceFocus on every modal/menu/popover one by one
// (whack-a-mole - the campaign flow alone has CustomModal + several MUI Menus +
// DatePicker Poppers), we install ONE capture-phase `focusin` guard while any QA
// portal is open. It runs BEFORE MUI's bubble-phase contain() handler and, for
// focus landing inside our portals ([data-qa-exclude]), calls
// stopImmediatePropagation() so MUI never sees the event and never steals focus.
// This covers all current and future MUI focus traps with no per-component changes.
let openCount = 0;
const listeners = new Set();

const captureFocusGuard = (event) => {
  const target = event.target;
  if (target && typeof target.closest === "function" && target.closest("[data-qa-exclude]")) {
    // Focus is inside a QA portal - prevent any MUI FocusTrap contain() handler
    // (registered on document, bubble phase) from running and refocusing its modal.
    event.stopImmediatePropagation();
  }
};

const installGuard = () => {
  // Capture phase so we run before MUI's bubble-phase focusin listener.
  document.addEventListener("focusin", captureFocusGuard, true);
};
const removeGuard = () => {
  document.removeEventListener("focusin", captureFocusGuard, true);
};

const notify = () => {
  const active = openCount > 0;
  listeners.forEach((fn) => {
    try {
      fn(active);
    } catch (e) {
      // a bad listener must not break focus management
    }
  });
};

export const acquireQaFocus = () => {
  openCount += 1;
  if (openCount === 1) installGuard();
  notify();
};

export const releaseQaFocus = () => {
  openCount = Math.max(0, openCount - 1);
  if (openCount === 0) removeGuard();
  notify();
};

export const isQaFocusActive = () => openCount > 0;

// Kept for the CustomModal subscription (belt-and-suspenders: it also flips
// disableEnforceFocus so MUI doesn't even try, in case the capture guard is ever
// bypassed). Harmless if unused.
export const subscribeQaFocus = (fn) => {
  listeners.add(fn);
  fn(openCount > 0);
  return () => listeners.delete(fn);
};
